import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  SunIcon,
  MoonIcon,
  GlobeAltIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightIcon,
  SparklesIcon,
  UserIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const logoLight = process.env.PUBLIC_URL + '/icons/sp-logo-blue.svg';
const logoDark = process.env.PUBLIC_URL + '/icons/sp-logo-white.svg';
const iconLight = process.env.PUBLIC_URL + '/icons/sp-fav-blue.svg';
const iconDark = process.env.PUBLIC_URL + '/icons/sp-fav-blue.svg';

const PublicHeader = ({ darkMode, toggleDarkMode }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('tr');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === selectedLanguage);

  const navLinks = [
    { name: 'Özellikler', href: '#ozellikler' },
    { name: 'Entegrasyonlar', href: '#entegrasyonlar' },
    { name: 'Sektörler', href: '#sektorler' },
    { name: 'Avantajlar', href: '#avantajlar' },
    { name: 'İletişim', href: '#iletisim' },
  ];

  return (
    <header 
      className="sticky top-0 z-50 px-2 sm:px-6 pb-1 transition-all"
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)'
      }}
    >
      <nav className={`relative flex items-center justify-between px-3 sm:px-6 py-2 sm:py-2.5 transition-all shadow-md duration-300 rounded-2xl ${
        darkMode ? 'bg-gray-900/95 border border-gray-800 text-white' : 'bg-white/95 border border-slate-200/80 text-slate-800'
      } backdrop-blur-md backdrop-saturate-150`}>
        {/* Logo */}
        <div className="flex items-center shrink-0 mr-2">
          <Link to="/" className="flex items-center group" title="Sectorpilot">
            <img
              src={darkMode ? logoDark : logoLight}
              className="h-7 sm:h-9 w-auto object-contain transition-all duration-200"
              alt="Sectorpilot"
              title="Sectorpilot"
            />
            <span className="sr-only">Sectorpilot</span>
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex items-center space-x-6">
          {navLinks.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className={`text-sm font-semibold transition-colors duration-200 ${
                darkMode
                  ? 'text-gray-300 hover:text-white hover:text-blue-400'
                  : 'text-slate-600 hover:text-blue-600'
              }`}
            >
              {item.name}
            </a>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-2.5 shrink-0">
          {/* Language Selector */}
          <Menu as="div" className="relative">
            <Menu.Button
              aria-label="Dil seçimi"
              className={`p-1 sm:p-1.5 rounded-xl transition-all flex items-center text-xs font-semibold ${
                darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span className="text-sm">{currentLanguage?.flag || '🌐'}</span>
              <ChevronDownIcon className="w-3 h-3 ml-0.5 text-slate-400" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-slate-200 dark:border-gray-700 py-1 z-50 focus:outline-none">
              {languages.map((language) => (
                <Menu.Item key={language.code}>
                  {({ active }) => (
                    <button
                      onClick={() => setSelectedLanguage(language.code)}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center ${
                        active ? 'bg-slate-100 dark:bg-gray-700 text-blue-600' : 'text-slate-700 dark:text-gray-300'
                      }`}
                    >
                      <span className="mr-2">{language.flag}</span>
                      {language.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            aria-label="Toggle dark mode"
            className={`p-1 sm:p-1.5 rounded-xl transition-all ${
              darkMode ? 'text-yellow-400 hover:bg-gray-800' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {darkMode ? <SunIcon className="w-4 h-4 sm:w-5 sm:h-5" /> : <MoonIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>

          {/* Login Button */}
          <Link
            to="/login"
            title="Giriş Yap"
            className={`inline-flex items-center justify-center px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold rounded-xl whitespace-nowrap transition-all duration-200 ${
              darkMode
                ? 'text-gray-200 border border-gray-700 hover:bg-gray-800'
                : 'text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            <UserIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-1 sm:mr-1.5" />
            <span>Giriş Yap</span>
          </Link>

          {/* Free Trial Button */}
          <Link
            to="/register"
            className="inline-flex items-center justify-center px-2 py-1.5 sm:px-3.5 sm:py-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white text-[11px] sm:text-xs font-bold rounded-xl whitespace-nowrap shadow-sm hover:shadow-md hover:shadow-blue-500/25 transition-all duration-200 hover:scale-[1.02]"
          >
            <SparklesIcon className="w-3.5 h-3.5 mr-1 hidden sm:inline" />
            <span>Başla</span>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-1 sm:p-1.5 rounded-xl text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Menüyü aç/kapat"
          >
            {mobileMenuOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer Navigation */}
      {mobileMenuOpen && (
        <div className={`lg:hidden mt-2 p-4 rounded-2xl border shadow-xl transition-all duration-300 ${
          darkMode ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-slate-200 text-slate-800'
        }`}>
          <div className="flex flex-col space-y-3">
            {/* Mobile-only Login Button */}
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className={`sm:hidden px-4 py-2.5 text-center text-sm font-bold rounded-xl border transition-colors ${
                darkMode
                  ? 'border-gray-700 text-gray-200 bg-gray-800/50 hover:bg-gray-800'
                  : 'border-slate-200 text-slate-800 bg-slate-50 hover:bg-slate-100'
              }`}
            >
              Giriş Yap
            </Link>

            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  darkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-slate-100 text-slate-700'
                }`}
              >
                {item.name}
              </a>
            ))}
            <div className="pt-2 border-t border-slate-200 dark:border-gray-800 flex items-center justify-between">
              <span className="text-xs text-slate-500 dark:text-gray-400">Dil Seçimi:</span>
              <div className="flex space-x-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setSelectedLanguage(lang.code)}
                    className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                      selectedLanguage === lang.code
                        ? 'bg-blue-600 text-white'
                        : darkMode ? 'bg-gray-800 text-gray-300' : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {lang.flag} {lang.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
